<script setup lang="ts">
import AppText from '~/components/common/AppText.vue'
</script>

<template>
	<div>
		<h1 class="mb-8 text-4xl md:text-5xl">Erläuterungen</h1>

		<h2 class="mb-8 text-3xl md:text-4xl">1.1 Begriffserläuterungen</h2>

		<h4 class="mb-4 text-3xl md:text-3xl">Klimavertäglichkeit</h4>
		<AppText id="TBS9" class="mb-8 block" />

		<h4 class="mb-4 text-3xl md:text-3xl">Nutzungsfreundlichkeit</h4>
		<AppText id="TBS10" class="mb-8 block" />

		<h2 class="mb-8 text-3xl md:text-4xl">1.2 Abkürzungen</h2>

		<div class="mb-8 overflow-x-auto">
			<table class="min-w-full table-auto border-collapse">
				<thead>
					<tr class="text-left">
						<th class="max-w-[200px] border-b px-4 py-2">Abkürzung</th>
						<th class="border-b px-4 py-2">Beschreibung</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td class="border-b px-4 py-2">CO<sub>2</sub></td>
						<td class="border-b px-4 py-2">Kohlendioxid</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">GWh</td>
						<td class="border-b px-4 py-2">Gigawattstunde</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">kWh</td>
						<td class="border-b px-4 py-2">Kilowattstunde</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">MWh</td>
						<td class="border-b px-4 py-2">Megawattstunde</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">PV</td>
						<td class="border-b px-4 py-2">Photovoltaik</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">THG</td>
						<td class="border-b px-4 py-2">Treibhausgasemissionen</td>
					</tr>
				</tbody>
			</table>
		</div>

		<h2 class="mb-8 text-3xl md:text-4xl">1.3 Wesentliche Datengrundlagen</h2>

		<div class="overflow-x-auto">
			<table class="min-w-full table-auto border-collapse">
				<thead>
					<tr class="text-left">
						<th class="border-b px-4 py-2">Datensatz</th>
						<th class="border-b px-4 py-2">Beschreibung</th>
						<th class="border-b px-4 py-2">Datenhalter bzw. Quelle</th>
					</tr>
				</thead>
				<tbody>
					<tr>
						<td class="border-b px-4 py-2">AGWR</td>
						<td class="border-b px-4 py-2">
							Adress-, Gebäude- und Wohnungsregister: Das AGWR enthält neben den Adressdaten auch
							Strukturdaten von Gebäuden, Wohnungen und sonstigen Nutzungseinheiten; Verwaltungsregister
						</td>
						<td class="border-b px-4 py-2">Zuständige Gemeinde, Land Salzburg</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Zeus Energie-ausweisdatenbank</td>
						<td class="border-b px-4 py-2">Zentrale Energieausweisdatenbank für das Bundesland Salzburg</td>
						<td class="border-b px-4 py-2">Land Salzburg (Ref. 4/04)</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Fördermanager</td>
						<td class="border-b px-4 py-2">
							Der Fördermanager für erneuerbare Energie (Photovoltaik, Solarthermie, Holzheizungen,
							Fernwärme, Wärmepumpen) unterstützt durch vollautomatische Abwicklung von Förderanträgen
							Landesstellen und Professionisten.
						</td>
						<td class="border-b px-4 py-2">Land Salzburg (Ref. 4/04)</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Gasleitungen</td>
						<td class="border-b px-4 py-2">Lage des Gasnetzes</td>
						<td class="border-b px-4 py-2">Land Salzburg (SAGIS), Salzburg Netz GmbH</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Wärmenetze</td>
						<td class="border-b px-4 py-2">Lage der Wärmenetze</td>
						<td class="border-b px-4 py-2">Land Salzburg (SAGIS), Salzburg Netz GmbH</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Gebäudemodell</td>
						<td class="border-b px-4 py-2">
							Digitale Abbildung des Gebäudebestandes im Bundesland Salzburg anhand bestehender
							(Geo-)Daten (z.B. Digitales Geländemodell, Digitales Oberflächenmodell, Digitale
							Katastermappe, AGWR, Zeus Energieausweisdatenbank, etc.). Allen Gebäuden wurden insbesondere
							Informationen zu Gebäudehüllqualität, Abmessungen, Nutzungen und Gebäudekonditionierung
							zugewiesen.
						</td>
						<td class="border-b px-4 py-2">
							Land Salzburg (SAGIS, Ref. 4/04), Modellentwicklung im Projekt GEL S/E/P und Zentrum Alpines
							Bauen (ZAB)
						</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Wärmebedarf</td>
						<td class="border-b px-4 py-2">
							Der Wärmebedarf wurde basierend auf dem Gebäudemodell und auf mit Verbrauchdaten kalibrierte
							Energiekennzahlen ermittelt. Die Modellierung umfasst Raumwärme und Warmwasser.
						</td>
						<td class="border-b px-4 py-2">
							Land Salzburg (SAGIS, Ref. 4/04), Modellentwicklung im Projekt GEL S/E/P
						</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Wärmebedarfsdichten</td>
						<td class="border-b px-4 py-2">
							Der Wärmebedarf je Gebäude wurde anhand eines Glättungsverfahrens gemittelt und so in
							Wärmebedarfsdichten übergeführt.
						</td>
						<td class="border-b px-4 py-2">
							Land Salzburg (SAGIS, Ref. 4/04), Modellweiterentwicklung im Projekt GEL S/E/P
						</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Wärmenetzpotenziale</td>
						<td class="border-b px-4 py-2">
							Darstellung jener Gebiete, wo eine leitungsgebundene Wärmeversorgung
							technisch/wirtschaftlich möglich erscheint. Vorwiegendes Kriterium ist die erforderliche
							Wärmebedarfsdichte von 40 GWh/km² (Stadt Salzburg) und außerhalb 22,5 GWh/km².
						</td>
						<td class="border-b px-4 py-2">
							Land Salzburg (SAGIS, Ref. 4/04), Modellweiterentwicklung im Projekt GEL S/E/P
						</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Solarpotenzial pro Jahr (Gesamte Flächen)</td>
						<td class="border-b px-4 py-2">
							Die ausgewiesene Globalstrahlung berücksichtigt Neigung, Orientierung sowie Nah- und
							Fernverschattung.
						</td>
						<td class="border-b px-4 py-2">Land Salzburg (SAGIS)</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Solarpotenzial pro Jahr (Hausflächen)</td>
						<td class="border-b px-4 py-2">
							Die ausgewiesene Globalstrahlung berücksichtigt Neigung, Orientierung sowie Nah- und
							Fernverschattung.
						</td>
						<td class="border-b px-4 py-2">Land Salzburg (SAGIS)</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Geothermie Eignungsgebiete</td>
						<td class="border-b px-4 py-2">
							Alle bekannten möglichen Einschränkungen zur Nutzung von Erdwärmesonden und Grundwasser sind
							hier zusammengefasst. Die Einstufung als nicht geeigneten Flächen (wie Schutzgebiete oder
							Gefahrenbereiche) erfolgte durch die Geologische Bundesanstalt (GBA) im Rahmen der Studie
							"GEL-SEP Salzburg - Informationssystem Oberflächennahe Geothermie für Salzburg".
						</td>
						<td class="border-b px-4 py-2">Land Salzburg (Ref. 4/04, SAGIS); Erstellung durch GBA</td>
					</tr>
					<tr>
						<td class="border-b px-4 py-2">Restriktionsflächen für alle anderen Erneuerbaren Potenziale</td>
						<td class="border-b px-4 py-2">
							Je nach Potenzialart werden unterschiedliche Flächen (wie Schutzgebiete oder
							Gefahrenbereiche) als nicht geeignet eingestuft. Diese Einstufung erfolgte im Rahmen des
							Projektes GEL S/E/P durch das Salzburger Institut für Raumordnung und Wohnen (SIR) und dem
							Land Salzburg (Ref. 4/04) unter Rücksprache bei zuständigen Referaten des Amtes der
							Salzburger Landesregierung.
						</td>
						<td class="border-b px-4 py-2">Geodaten: Land Salzburg (SAGIS)</td>
					</tr>
				</tbody>
			</table>
		</div>
		<h2 class="my-8 text-3xl md:text-4xl">1.4 Datenschutz, Rechtlicher Hinweis und Haftungsausschluss</h2>

		<p>
			Alle Hinweise und Informationen, die im EnergieKompass bereitgestellt werden, wurden automatisch generiert
			und sind deshalb für den einzelnen Standort nicht individuell überprüft. Die Analysen basieren auf
			flächendeckend verfügbaren Datenquellen (siehe dazu 1.3) und werden auf der Grundlage differenzierter
			wissenschaftlicher Methoden, welche in einem großen nationalen Forschungsprojekt (www.waermeplanung.at)
			unter Beteiligung wesentlicher österreichischer Forschungsinstitutionen entwickelt wurden, erstellt. Sie
			stellen den bestmöglichen Stand des Wissens auf Basis automatisierter Berechnungen dar.
		</p>
		<br />
		<p>
			Die Informationen des EnergieKompass geben eine Erstorientierung, ersetzen jedoch keinesfalls Expertisen (zB
			technische Machbarkeit bei dem spezifischen Gebäude) und Sachverständigeninformationen (zB zur Geologie). Es
			wird ausdrücklich darauf hingewiesen, dass für die finale Beurteilung der Machbarkeit ein qualifizierter
			Fachplaner und/oder Installateur beizuziehen ist. Es kann nicht ausgeschlossen werden, dass neben den
			angeführten auch bisher nicht bekannte Risiken und Restriktion im Zusammenhang mit der Umsetzbarkeit der
			jeweiligen Wärmeversorgungssysteme auftreten. Es wird keine Gewähr - weder ausdrücklich noch stillschweigend
			- für die Vollständigkeit, Richtigkeit oder Qualität der bereitgestellten Informationen übernommen. In
			keinem Fall wird für Schäden, auch für Schäden Dritter, die sich aus der Verwendung der abgerufenen
			Informationen ergeben, eine Haftung übernommen.
		</p>
	</div>
</template>
